import { useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button, IconResetClock, IconTasks, SkeletonBlock, SkeletonText, StatusBadge, cn } from '@taki/ui';
import { contactDisplayName } from '@taki/data-model';
import type { Asset, Contact, Todo } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import type { PersonneVue } from '@taki/ui';
import { CarteInfo, ChampTexte, Choix, Section, instantDe, libelleDeLEtape, optionsDe, sac, tonDeLEtape } from './morceaux.js';
import type { Fiche } from './morceaux.js';
import { ageAuJour, datePureEnTexte, valeurEnTexte } from './temps.js';
import { ecartSurUnAn } from './apercu.js';
import type { LigneDHistorique, PointDePoids } from './apercu.js';
import { alerteDuRappel } from './carnet.js';
import type { LigneDuCarnet } from './carnet.js';
import { kiloEnTexte } from './nombre.js';
import { etatEnPastille } from './OngletCarnet.js';
import { PanneauSuivi } from './PanneauSuivi.js';
import type { TacheDuSuivi } from './PanneauSuivi.js';
import { ResumeDeLExamen } from './ResumeDeLExamen.js';
import { echeanceEnMots } from './suivi.js';

/**
 * LES BRIQUES DU DOSSIER DE CONSULTATION : la carte patient, les cadres de
 * l'apercu, l'historique, les taches. Aucune n'appelle le SDK pour ecrire : les
 * gestes arrivent par les props.
 */

/** Les libelles du sexe, ceux du jeu partage des animaux (champ `sex`, aligne sur Patients). */
const SEXES = new Map<string, string>(optionsDe('animal', 'sex').map((o) => [o.value, o.label]));

/** « Chien · Berger allemand · Fauve · Mâle ». */
export function identiteDe(fiche: Record<string, unknown>): string {
  return [valeurEnTexte(fiche['species']), valeurEnTexte(fiche['breed']), valeurEnTexte(fiche['coat']), SEXES.get(String(fiche['sex'] ?? '')) ?? '']
    .filter((x) => x !== '')
    .join(' · ');
}

/** Le squelette d'un chiffre de la carte patient, a la hauteur de son texte. */
function ChiffreEnChargement(): JSX.Element {
  return <SkeletonBlock w="5rem" h="1.25rem" />;
}

/**
 * LA CARTE PATIENT ET SES CHIFFRES : le nom, l'identite, le proprietaire, puis
 * l'age, le poids, la derniere visite et le prochain rappel. `ligne` porte ce
 * que l'ecran ajoute sous l'identite (le motif du jour en consultation). Les
 * boutons vivent sur la ligne de navigation au-dessus : dans la carte, ils
 * mangeaient le nom de l'animal (recette du 26/10).
 */
export function EnTeteDuPatient({
  animal,
  proprietaire,
  quand,
  derniere,
  prochain,
  chargement = false,
  ligne,
}: {
  animal: Asset | null;
  proprietaire: Contact | null;
  quand: Date;
  derniere: Fiche | undefined;
  prochain: (LigneDuCarnet & { prochaine: string }) | undefined;
  /** Vrai tant que l'animal, son historique ou son carnet ne sont pas lus : les chiffres montrent un squelette. */
  chargement?: boolean;
  ligne?: ReactNode;
}): JSX.Element {
  const fiche = sac(animal);
  const identite = identiteDe(fiche);
  const alerte = prochain === undefined ? null : alerteDuRappel(prochain.etat);
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]">
      <div className="flex min-w-0 flex-col gap-2 rounded-md border border-border-soft bg-surface px-5 py-4">
        <h2 className="truncate text-2xl font-bold text-ink-900">{animal?.name ?? 'Animal'}</h2>
        <p className="text-sm text-ink-500">{identite === '' ? 'Espèce non renseignée' : identite}</p>
        {ligne === undefined ? null : <p className="text-xs text-ink-500">{ligne}</p>}
        <p className="truncate text-sm text-ink-700">
          {proprietaire === null
            ? 'Propriétaire non renseigné'
            : [contactDisplayName(proprietaire), proprietaire.phone ?? ''].filter((x) => x !== '').join(' · ')}
        </p>
      </div>
      <CarteInfo libelle="Âge">{chargement ? <ChiffreEnChargement /> : ageAuJour(fiche['birthDate'], quand) || '·'}</CarteInfo>
      <CarteInfo libelle="Poids">
        {chargement ? <ChiffreEnChargement /> : typeof fiche['weightKg'] === 'number' ? kiloEnTexte(fiche['weightKg']) : '·'}
      </CarteInfo>
      <CarteInfo libelle="Dernière visite">
        {chargement ? <ChiffreEnChargement /> : derniere === undefined ? 'Aucune' : (instantDe(derniere)?.toLocaleDateString('fr-BE') ?? '·')}
      </CarteInfo>
      <CarteInfo libelle="Prochain rappel">
        {chargement ? (
          <ChiffreEnChargement />
        ) : prochain === undefined ? (
          'Aucun'
        ) : (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn(prochain.etat === 'retard' && 'text-danger')}>{datePureEnTexte(prochain.prochaine)}</span>
            {alerte === null ? null : <StatusBadge label={alerte.label} tone={alerte.tone} />}
          </span>
        )}
      </CarteInfo>
    </div>
  );
}

/**
 * UN CADRE DE L'APERCU (recette du fondateur le 26/10 : les zones ne se
 * distinguaient pas) : une bordure, un bandeau de titre avec son icone, le
 * contenu dessous. `accent` colore le bandeau de la zone a traiter aujourd'hui.
 */
export function CadreDApercu({ icone, titre, accent = false, children }: { icone: ReactNode; titre: string; accent?: boolean; children: ReactNode }): JSX.Element {
  return (
    <section className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-surface shadow-sm">
      <h3
        className={cn(
          'flex items-center gap-2 border-b px-5 py-3 text-base font-semibold',
          accent ? 'border-accent-border bg-accent-tint text-accent-ink' : 'border-border-soft bg-surface-1 text-ink-900',
        )}
      >
        <span aria-hidden="true" className="flex">
          {icone}
        </span>
        {titre}
      </h3>
      <div className="flex flex-col gap-3 px-5 py-3">{children}</div>
    </section>
  );
}

/** Une ligne de l'apercu : un titre court a gauche, le contenu a droite. */
export function LigneDApercu({ titre, alerte = false, children }: { titre: string; alerte?: boolean; children: ReactNode }): JSX.Element {
  return (
    <div className="flex gap-3 border-b border-border-soft py-2 last:border-b-0">
      <dt className={cn('w-32 shrink-0 text-ink-500', alerte && 'text-danger')}>{titre}</dt>
      <dd className="min-w-0 flex-1 text-ink-900">{children}</dd>
    </div>
  );
}

/**
 * LA COURBE DU POIDS de l'apercu : le dernier poids, son ecart sur un an et
 * une ligne simple, sans axe. Un seul point (le poids de la fiche) s'affiche
 * seul, sans ligne.
 */
export function CourbeDuPoids({ courbe }: { courbe: readonly PointDePoids[] }): JSX.Element {
  const dernier = courbe[courbe.length - 1];
  const ecart = ecartSurUnAn(courbe);
  const largeur = 200;
  const hauteur = 48;
  const min = Math.min(...courbe.map((p) => p.kg));
  const max = Math.max(...courbe.map((p) => p.kg));
  const debut = courbe[0]?.quand.getTime() ?? 0;
  const duree = Math.max(1, (dernier?.quand.getTime() ?? 0) - debut);
  const points = courbe
    .map((p) => {
      const x = ((p.quand.getTime() - debut) / duree) * largeur;
      const y = max === min ? hauteur / 2 : hauteur - 4 - ((p.kg - min) / (max - min)) * (hauteur - 8);
      return `${Math.round(x)},${Math.round(y)}`;
    })
    .join(' ');
  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-semibold text-ink-500">Poids</span>
      {dernier === undefined ? (
        <span className="text-ink-400">Jamais pesé</span>
      ) : (
        <>
          <span className="text-2xl font-bold tabular-nums text-ink-900">{kiloEnTexte(dernier.kg)}</span>
          {courbe.length > 1 ? (
            <svg viewBox={`0 0 ${largeur} ${hauteur}`} className="h-12 w-full text-accent-dot" aria-hidden="true">
              <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          ) : null}
          <span className="text-xs text-ink-500">
            {ecart === null
              ? `${courbe.length} pesée${courbe.length > 1 ? 's' : ''}`
              : `${ecart > 0 ? '+' : ''}${kiloEnTexte(ecart)} en un an · ${courbe.length} pesées`}
          </span>
        </>
      )}
    </div>
  );
}

/** « À faire ou proposer » : les protocoles du carnet a proposer et les taches ouvertes. */
export function CadreAFaire({
  chargement = false,
  titre,
  aProposer,
  tachesOuvertes,
  nomDePersonne,
}: {
  /** Vrai tant que le carnet et les taches ne sont pas lus : « Rien en attente » serait un mensonge. */
  chargement?: boolean;
  titre: string;
  aProposer: readonly LigneDuCarnet[];
  tachesOuvertes: readonly Todo[];
  nomDePersonne: (personId: unknown) => string;
}): JSX.Element {
  return (
    <CadreDApercu icone={<IconTasks size={18} />} titre={titre} accent>
      {chargement ? <SkeletonText lines={3} /> : null}
      {!chargement && aProposer.length === 0 && tachesOuvertes.length === 0 ? <p className="text-sm text-ink-500">Rien en attente.</p> : null}
      <dl className="flex flex-col text-sm">
        {aProposer.map((l) => (
          <LigneDApercu key={l.protocole} titre={etatEnPastille(l.etat, l.obligatoire).label} alerte={l.etat === 'retard' || l.etat === 'jamais'}>
            {l.protocole}
            {l.prochaine === null ? ', jamais fait' : ` (${datePureEnTexte(l.prochaine)})`}
          </LigneDApercu>
        ))}
        {tachesOuvertes.map((t) => {
          const echeance = t.dueAt === undefined ? null : echeanceEnMots(t.dueAt, new Date(), false);
          return (
            <LigneDApercu key={t.id} titre="Tâche" alerte={echeance?.retard === true}>
              {valeurEnTexte(sac(t)['libelle'])}
              {echeance === null ? '' : `, ${echeance.texte}`}
              {nomDePersonne(sac(t)['assigne']) === '' ? '' : ` · ${nomDePersonne(sac(t)['assigne'])}`}
            </LigneDApercu>
          );
        })}
      </dl>
    </CadreDApercu>
  );
}

/** « Son historique » : les dernieres visites, leurs anomalies d'examen et leur traitement, a cote de la courbe du poids. */
export function CadreSonHistorique({
  chargement = false,
  lignes,
  poids,
  vide,
  onOuvrirConsultation,
}: {
  /** Vrai tant que les visites, les examens et les pesees ne sont pas lus. */
  chargement?: boolean;
  lignes: readonly LigneDHistorique[];
  poids: readonly PointDePoids[];
  vide: string;
  onOuvrirConsultation: (id: string) => void;
}): JSX.Element {
  return (
    <CadreDApercu icone={<IconResetClock size={18} />} titre="Son historique">
      {chargement ? (
        <SkeletonText lines={5} />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_14rem]">
          {lignes.length === 0 ? (
            <p className="text-sm text-ink-500">{vide}</p>
          ) : (
            <ul className="flex flex-col">
              {lignes.map((l) => (
                <li key={l.id} className="flex flex-col gap-1 border-b border-border-soft py-3 text-sm last:border-b-0">
                  <button
                    type="button"
                    onClick={() => onOuvrirConsultation(l.id)}
                    className="flex flex-wrap items-baseline gap-x-3 text-left hover:underline focus-visible:outline-none"
                  >
                    <span className="tabular-nums font-semibold text-ink-900">{l.quand.toLocaleDateString('fr-BE')}</span>
                    <span className="text-ink-900">{[l.motif, l.diagnostic].filter((x) => x !== '').join(' · ')}</span>
                    {l.veterinaire === '' ? null : <span className="text-ink-500">{l.veterinaire}</span>}
                  </button>
                  {l.anomalies.length > 0 ? (
                    <p className="text-danger">Examen : {l.anomalies.join(' ; ')}</p>
                  ) : l.examenNormal ? (
                    <p className="text-ink-500">Examen normal</p>
                  ) : null}
                  {l.therapie === '' ? null : <p className="text-ink-700">Traitement : {l.therapie}</p>}
                </li>
              ))}
            </ul>
          )}
          <CourbeDuPoids courbe={poids} />
        </div>
      )}
    </CadreDApercu>
  );
}

const COMPTE_RENDU_TEXTE: readonly { cle: string; libelle: string }[] = [
  { cle: 'anamnesis', libelle: 'Anamnèse' },
  { cle: 'exam', libelle: 'Examen clinique' },
  { cle: 'diagnosis', libelle: 'Diagnostic' },
  { cle: 'therapy', libelle: 'Thérapie' },
  { cle: 'prognosis', libelle: 'Pronostic' },
];

/** L'onglet Historique : une consultation passee se deplie en lecture sous sa ligne, et s'ouvre dans son propre dossier. */
export function OngletHistorique({
  sdk,
  consultations,
  vide,
  onOuvrirConsultation,
}: {
  sdk: TakiSdk;
  consultations: readonly Fiche[];
  vide: string;
  onOuvrirConsultation: (id: string) => void;
}): JSX.Element {
  const [depliee, setDepliee] = useState<string | null>(null);
  return (
    <Section titre="Historique">
      {consultations.length === 0 ? (
        <p className="text-sm text-ink-500">{vide}</p>
      ) : (
        <div className="flex flex-col">
          {consultations.map((c) => {
            const cid = String(c['id']);
            const ouverte = depliee === cid;
            return (
              <div key={cid} className="border-b border-border-soft">
                <button
                  type="button"
                  onClick={() => setDepliee(ouverte ? null : cid)}
                  className="flex w-full items-center gap-4 py-3 text-left text-sm hover:bg-surface-3 focus-visible:outline-none"
                >
                  <span className="w-24 shrink-0 tabular-nums text-ink-700">{instantDe(c)?.toLocaleDateString('fr-BE') ?? ''}</span>
                  <span className="w-32 shrink-0 text-ink-900">{valeurEnTexte(sac(c)['reason'])}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-700">{valeurEnTexte(sac(c)['diagnosis']) || valeurEnTexte(sac(c)['description'])}</span>
                  <StatusBadge label={libelleDeLEtape(sac(c)['stage'])} tone={tonDeLEtape(sac(c)['stage'])} />
                </button>
                {ouverte ? (
                  <div className="grid grid-cols-1 gap-4 pb-4 lg:grid-cols-2">
                    {/* Dans l'ordre du compte rendu : l'anamnese, l'examen en liste, puis le reste. */}
                    {COMPTE_RENDU_TEXTE.slice(0, 1).map((champ) => (
                      <ChampTexte key={champ.cle} libelle={champ.libelle} valeur={sac(c)[champ.cle]} lecture />
                    ))}
                    <ResumeDeLExamen sdk={sdk} consultationId={cid} />
                    {COMPTE_RENDU_TEXTE.slice(1).map((champ) => (
                      <ChampTexte key={champ.cle} libelle={champ.libelle} valeur={sac(c)[champ.cle]} lecture />
                    ))}
                    <Choix libelle="Examens complémentaires" options={[]} valeur={sac(c)['extraExams']} lecture />
                    <Choix libelle="Suivi" options={[]} valeur={sac(c)['followUp']} lecture />
                    <div>
                      <Button variant="secondary" size="sm" onClick={() => onOuvrirConsultation(cid)}>
                        Ouvrir ce dossier
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

/**
 * LES TACHES EN TABLEAU (recette du fondateur le 26/10 : tout ce qu'on sait de
 * la tache, lisible d'un coup, pas rejete a droite) : fait, la tache, prevue le
 * avec « demain » ou « en retard », pour qui, l'etat, et la fleche qui ouvre la
 * fiche de la tache.
 */
export function ListeDeTaches({
  taches,
  nomDePersonne,
  onBasculer,
  onOuvrir,
}: {
  taches: readonly Todo[];
  nomDePersonne: (personId: unknown) => string;
  onBasculer: (tache: Todo) => void;
  onOuvrir: (tache: Todo) => void;
}): JSX.Element {
  const maintenant = new Date();
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border-soft text-left text-xs text-ink-500">
          <th className="w-8 py-2 font-semibold">
            <span className="sr-only">Fait</span>
          </th>
          <th className="py-2 font-semibold">Tâche</th>
          <th className="w-48 py-2 font-semibold">Prévue le</th>
          <th className="w-40 py-2 font-semibold">Pour</th>
          <th className="w-28 py-2 font-semibold">État</th>
          <th className="w-8" />
        </tr>
      </thead>
      <tbody>
        {taches.map((t) => {
          const faite = t.status === 'termine';
          const echeance = t.dueAt === undefined ? null : echeanceEnMots(t.dueAt, maintenant, faite);
          const pour = nomDePersonne(sac(t)['assigne']);
          return (
            <tr key={t.id} className="border-b border-border-soft">
              <td className="py-2">
                <input type="checkbox" checked={faite} onChange={() => onBasculer(t)} aria-label="Fait" className="h-4 w-4 accent-ink-900" />
              </td>
              <td className={cn('py-2 pr-3', faite ? 'text-ink-400 line-through' : 'text-ink-900')}>{valeurEnTexte(sac(t)['libelle'])}</td>
              <td className="py-2 pr-3">
                {t.dueAt === undefined || echeance === null ? (
                  <span className="text-ink-400">Pas de date</span>
                ) : (
                  <span className="flex flex-col">
                    <span className="tabular-nums text-ink-900">{t.dueAt.toLocaleDateString('fr-BE')}</span>
                    <span className={cn('text-xs', echeance.retard ? 'text-danger' : 'text-ink-500')}>{echeance.texte}</span>
                  </span>
                )}
              </td>
              <td className={cn('py-2 pr-3', pour === '' ? 'text-ink-400' : 'text-ink-900')}>{pour === '' ? 'Personne' : pour}</td>
              <td className="py-2">
                <StatusBadge
                  label={faite ? 'Faite' : echeance?.retard === true ? 'En retard' : 'À faire'}
                  tone={faite ? 'success' : echeance?.retard === true ? 'danger' : 'neutral'}
                />
              </td>
              <td className="py-2 text-right">
                <Button variant="ghost" size="sm" aria-label="Ouvrir la tâche" onClick={() => onOuvrir(t)}>
                  ›
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** L'onglet Taches : toutes les taches de l'animal, et une nouvelle tache libre. */
export function OngletTaches({
  taches,
  animal,
  personnes,
  assigneParDefaut,
  nomDePersonne,
  onBasculer,
  onOuvrir,
  onCreer,
}: {
  taches: readonly Todo[];
  animal: string;
  personnes: readonly PersonneVue[];
  assigneParDefaut: string;
  nomDePersonne: (personId: unknown) => string;
  onBasculer: (tache: Todo) => void;
  onOuvrir: (tache: Todo) => void;
  onCreer: (tache: TacheDuSuivi) => Promise<boolean>;
}): JSX.Element {
  return (
    <Section titre="Tâches du dossier">
      {taches.length === 0 ? <p className="text-sm text-ink-500">Aucune tâche pour cet animal.</p> : null}
      <ListeDeTaches taches={taches} nomDePersonne={nomDePersonne} onBasculer={onBasculer} onOuvrir={onOuvrir} />
      <span className="text-xs font-semibold text-ink-500">Nouvelle tâche</span>
      <PanneauSuivi suivi="Autre" depuis={new Date()} animal={animal} vaccins={0} personnes={personnes} assigneParDefaut={assigneParDefaut} onCreer={onCreer} />
    </Section>
  );
}
